import type { SceneKeyframe } from '../scene-clock'

/**
 * Pure timeline and geometry for the landlord "Fake Statements Look Real"
 * scene. A magnifier sweeps a bank statement row by row (label, then amount),
 * then the statement gives way to the TrueTenant card whose rows come
 * straight from the institution and get ticked one by one.
 *
 * The component measures the row centres once per play and drives motion
 * values from `lensPointAt` + `lensTransforms` every frame; everything that
 * can be computed without the DOM lives here and is unit tested.
 */

export interface StatementRow {
	date: string
	label: string
	amount: string
}

/** The statement the magnifier inspects (demo data, not product copy). */
export const STATEMENT = {
	bank: 'Northbank · Chequing',
	title: 'Bank Statement',
	file: 'statement.pdf',
	rows: [
		{ date: 'Aug 01', label: 'Payroll Deposit', amount: '+$10,000.00' },
		{ date: 'Aug 01', label: 'Rent e-Transfer', amount: '-$2,500.00' },
		{ date: 'Aug 03', label: 'Grocery Store', amount: '-$86.42' },
		{ date: 'Aug 09', label: 'Phone Bill', amount: '-$65.00' },
		{ date: 'Aug 15', label: 'Gas Station', amount: '-$54.20' },
	],
	account: 'Account 0042-118-4821',
	balance: 'Balance $6,214.57',
} as const satisfies {
	bank: string
	title: string
	file: string
	rows: readonly StatementRow[]
	account: string
	balance: string
}

/** The TrueTenant card that replaces it. */
export const VERIFIED = {
	title: 'Straight From The Institution',
	chip: 'Income and Rent Only',
	rows: [
		{ date: 'Jul 01', label: 'Payroll Deposit', amount: '$10,000.00' },
		{ date: 'Jul 01', label: 'Rent e-Transfer', amount: '$2,500.00' },
		{ date: 'Aug 01', label: 'Payroll Deposit', amount: '$10,000.00' },
		{ date: 'Aug 01', label: 'Rent e-Transfer', amount: '$2,500.00' },
		{ date: 'Sep 01', label: 'Payroll Deposit', amount: '$10,000.00' },
		{ date: 'Sep 01', label: 'Rent e-Transfer', amount: '$2,500.00' },
	],
} as const satisfies {
	title: string
	chip: string
	rows: readonly StatementRow[]
}

/** Lens disc radius (160px circle). */
export const LENS_RADIUS = 80
/** Magnification inside the lens. */
export const LENS_SCALE = 1.4
/** The ring outside the disc: 5px white plus 1.5px ink. */
export const LENS_RING = 6.5
/** The handle: a 14x64 ink bar hung at -45deg from the lower right. */
export const LENS_HANDLE = { width: 14, length: 64, offset: 0.62 } as const

export interface LensTiming {
	/** Lens fades in and starts moving this long after activation. */
	startDelay: number
	labelHold: number
	labelMove: number
	amountHold: number
	/** Travel from one row's amount to the next row's label. */
	amountMove: number
	/** Pause after the sweep before the TrueTenant card appears. */
	cardDelay: number
	/** First check mark after the card, then one every `checkStep`. */
	checkDelay: number
	checkStep: number
	loopMs: number
}

export const LENS_TIMING: LensTiming = {
	startDelay: 600,
	labelHold: 320,
	labelMove: 650,
	amountHold: 420,
	amountMove: 600,
	cardDelay: 500,
	checkDelay: 500,
	checkStep: 300,
	loopMs: 16_000,
}

/* ---------- phases ---------- */

export type LensPhase = 'idle' | 'sweep' | 'card' | `check-${number}`

/** How long the magnifier takes to visit every row, label then amount. */
export function lensSweepDuration(
	rowCount: number,
	timing: LensTiming = LENS_TIMING,
) {
	if (rowCount <= 0) return 0
	const perRow = timing.labelHold + timing.labelMove + timing.amountHold
	return rowCount * perRow + (rowCount - 1) * timing.amountMove
}

/**
 * The scene's coarse timeline for `useSceneClock`: idle, the sweep, the card,
 * then one phase per check mark. The lens position itself is continuous and
 * comes from `lensPointAt`.
 */
export function lensSceneKeyframes(
	statementRows: number,
	verifiedRows: number,
	timing: LensTiming = LENS_TIMING,
): SceneKeyframe<LensPhase>[] {
	const cardAt =
		timing.startDelay +
		lensSweepDuration(statementRows, timing) +
		timing.cardDelay
	const frames: SceneKeyframe<LensPhase>[] = [
		{ at: 0, phase: 'idle' },
		{ at: timing.startDelay, phase: 'sweep' },
		{ at: cardAt, phase: 'card' },
	]
	for (let i = 1; i <= verifiedRows; i++) {
		frames.push({
			at: cardAt + timing.checkDelay + (i - 1) * timing.checkStep,
			phase: `check-${i}`,
		})
	}
	return frames
}

/** How many verified rows carry their check mark in this phase. */
export function checksShown(phase: LensPhase): number {
	const match = /^check-(\d+)$/.exec(phase)
	return match ? Number(match[1]) : 0
}

/** True while the magnifier is visible. */
export function lensVisible(phase: LensPhase) {
	return phase === 'sweep'
}

/** True once the TrueTenant card has replaced the statement. */
export function verifiedShown(phase: LensPhase) {
	return phase === 'card' || checksShown(phase) > 0
}

/** Milliseconds into the sweep for a point in the loop (negative before it). */
export function sweepTimeAt(
	elapsedMs: number,
	timing: LensTiming = LENS_TIMING,
) {
	const loop = timing.loopMs
	const inLoop = ((elapsedMs % loop) + loop) % loop
	return inLoop - timing.startDelay
}

/* ---------- geometry ---------- */

export interface LensPoint {
	x: number
	y: number
}

/** A DOMRect-like box; only these four fields are read. */
export interface Box {
	left: number
	top: number
	width: number
	height: number
}

export interface Bounds {
	left: number
	top: number
	right: number
	bottom: number
}

/** Where the lens must centre for one row, relative to the statement card. */
export interface RowCentres {
	y: number
	labelX: number
	amountX: number
}

export interface LensKeyframe extends LensPoint {
	t: number
}

function centreX(box: Box, origin: Box) {
	return box.left - origin.left + box.width / 2
}
function centreY(box: Box, origin: Box) {
	return box.top - origin.top + box.height / 2
}

/**
 * Row centres from measured boxes, relative to the card's box. Translation of
 * the whole card (the reveal animation) cancels out because every box is
 * taken relative to the card in the same frame.
 */
export function rowCentresFromBoxes(
	card: Box,
	rows: readonly { row: Box; label: Box; amount: Box }[],
): RowCentres[] {
	return rows.map(({ row, label, amount }) => ({
		y: centreY(row, card),
		labelX: centreX(label, card),
		amountX: centreX(amount, card),
	}))
}

/** A box's edges relative to another box's top-left corner. */
export function boundsRelativeTo(origin: Box, box: Box): Bounds {
	return {
		left: box.left - origin.left,
		top: box.top - origin.top,
		right: box.left - origin.left + box.width,
		bottom: box.top - origin.top + box.height,
	}
}

/** How far the drawn magnifier extends from its centre on each side. */
export function lensReach() {
	const { width, length, offset } = LENS_HANDLE
	const diagonal = Math.SQRT1_2
	return {
		/** Left and top: the disc plus its ring. */
		near: LENS_RADIUS + LENS_RING,
		/** Right and bottom: the tip of the handle (rounded end included). */
		far: LENS_RADIUS * offset + length * diagonal + width / 2,
	}
}

function clampAxis(value: number, min: number, max: number) {
	if (min > max) return (min + max) / 2
	return Math.min(max, Math.max(min, value))
}

/**
 * Keeps the whole magnifier (ring and handle) inside `bounds`, the tile's
 * clip box. The reference lets the handle poke out of the tile on phones.
 */
export function clampLensCentre(point: LensPoint, bounds: Bounds): LensPoint {
	const { near, far } = lensReach()
	return {
		x: clampAxis(point.x, bounds.left + near, bounds.right - far),
		y: clampAxis(point.y, bounds.top + near, bounds.bottom - far),
	}
}

export function clampRowCentres(
	rows: readonly RowCentres[],
	bounds: Bounds,
): RowCentres[] {
	return rows.map((row) => {
		const label = clampLensCentre({ x: row.labelX, y: row.y }, bounds)
		const amount = clampLensCentre({ x: row.amountX, y: row.y }, bounds)
		return { y: label.y, labelX: label.x, amountX: amount.x }
	})
}

/**
 * The sweep as position keyframes: for each row the lens sits on the label,
 * glides to the amount, sits again, then glides to the next row's label.
 * Time 0 is the start of the sweep (after `startDelay`).
 */
export function lensKeyframes(
	rows: readonly RowCentres[],
	timing: LensTiming = LENS_TIMING,
): LensKeyframe[] {
	const frames: LensKeyframe[] = []
	let t = 0
	const dwell = (x: number, y: number, hold: number, move: number) => {
		frames.push({ t, x, y })
		t += hold
		frames.push({ t, x, y })
		t += move
	}
	rows.forEach((row, index) => {
		const last = index === rows.length - 1
		dwell(row.labelX, row.y, timing.labelHold, timing.labelMove)
		dwell(row.amountX, row.y, timing.amountHold, last ? 0 : timing.amountMove)
	})
	return frames
}

/** Cosine ease-in-out, 0..1 to 0..1 (the reference's easing). */
export function cosineEase(u: number) {
	const k = Math.min(1, Math.max(0, u))
	return (1 - Math.cos(Math.PI * k)) / 2
}

/** Where the lens centre is `ms` into the sweep; clamped to the path's ends. */
export function lensPointAt(
	ms: number,
	frames: readonly LensKeyframe[],
): LensPoint {
	const first = frames[0]
	if (!first) return { x: 0, y: 0 }
	if (ms <= first.t) return { x: first.x, y: first.y }
	for (let j = 1; j < frames.length; j++) {
		const b = frames[j] as LensKeyframe
		if (ms > b.t) continue
		const a = frames[j - 1] as LensKeyframe
		const u = b.t === a.t ? 1 : (ms - a.t) / (b.t - a.t)
		const v = cosineEase(u)
		return { x: a.x + (b.x - a.x) * v, y: a.y + (b.y - a.y) * v }
	}
	const last = frames[frames.length - 1] as LensKeyframe
	return { x: last.x, y: last.y }
}

function px(n: number) {
	return `${Math.round(n * 100) / 100}px`
}

/**
 * CSS transforms for a lens centred at `centre` (card coordinates): the disc,
 * the magnified copy inside it (so the point under the centre is what is
 * magnified) and the handle. Transforms only, so a frame never touches layout.
 */
export function lensTransforms(centre: LensPoint) {
	const R = LENS_RADIUS
	const S = LENS_SCALE
	const { width, offset } = LENS_HANDLE
	return {
		lens: `translate(${px(centre.x - R)}, ${px(centre.y - R)})`,
		mag: `translate(${px(R - centre.x * S)}, ${px(R - centre.y * S)}) scale(${S})`,
		handle: `translate(${px(centre.x + R * offset - width / 2)}, ${px(centre.y + R * offset)}) rotate(-45deg)`,
	}
}
